import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import jobOpeningsApi from '../../../middleware/api/Recruitment/JobOpenings/jobOpeningsApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  GetAllJobVacanciesList,
  GetAllJobVacanciesProps,
  GetAllTechnology,
  JobOpeningsSliceState,
  JobVacancy,
  JobVacancyAuditList,
  VendorResponse,
} from '../../../types/Recruitment/JobOpenings/jobOpeningsTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'

const getAllJobVacancies = createAsyncThunk(
  'jobOpenings/getAllJobVacancies',
  async (props: GetAllJobVacanciesProps, thunkApi) => {
    try {
      return await jobOpeningsApi.getAllJobVacancies(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllTechnology = createAsyncThunk(
  'jobOpenings/getAllTechnology',
  async (_, thunkApi) => {
    try {
      return await jobOpeningsApi.getAllTechnology()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addJobVacancy = createAsyncThunk(
  'jobOpenings/addJobVacancy',
  async (data: JobVacancy, thunkApi) => {
    try {
      return await jobOpeningsApi.addJobVacancy(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteJobVacancy = createAsyncThunk(
  'jobOpenings/deleteJobVacancy',
  async (jobvacancyId: number, thunkApi) => {
    try {
      return await jobOpeningsApi.deleteJobVacancy(jobvacancyId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getJobOpeningById = createAsyncThunk(
  'jobOpenings/getJobOpeningById',
  async (jobVacancyId: number, thunkApi) => {
    try {
      return await jobOpeningsApi.getJobOpeningById(jobVacancyId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getJobVacancyAudit = createAsyncThunk(
  'jobOpenings/getJobVacancyAudit',
  async (jobVacancyId: number, thunkApi) => {
    try {
      return await jobOpeningsApi.getJobVacancyAudit(jobVacancyId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateJobVacancy = createAsyncThunk(
  'jobOpenings/updateJobVacancy',
  async (data: GetAllJobVacanciesList, thunkApi) => {
    try {
      return await jobOpeningsApi.updateJobVacancy(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const isCandidateMappedWithJob = createAsyncThunk(
  'jobOpenings/isCandidateMappedWithJob',
  async (jobCode: string, thunkApi) => {
    try {
      return await jobOpeningsApi.isCandidateMappedWithJob(jobCode)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getActiveVendorsList = createAsyncThunk(
  'assetManagement/getActiveVendorsList',
  async (_, thunkApi) => {
    try {
      return await jobOpeningsApi.getActiveVendorsList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const editVendorJobDetails = createAsyncThunk(
  'jobOpenings/editVendorJobDetails',
  async (id: number, thunkApi) => {
    try {
      return await jobOpeningsApi.editVendorJobDetails(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialJobOpeningsState: JobOpeningsSliceState = {
  isLoading: ApiLoadingState.idle,
  listSize: 0,
  getAllTechnology: [],
  getAllJobVacancies: [],
  getJobOpeningById: {} as GetAllJobVacanciesList,
  getJobVacancyAuditList: [],
  getJobVacancyAudit: {} as JobVacancyAuditList,
  status: 'Open',
  searchString: '',
  paginationCache: {
    currentPage: 0,
    pageSize: 20,
    searchJobTitle: '',
    status: '',
  },
  isProc: false,
  isFound: false,
  getVendorDetails: [],
  vendor: false,
}

const jobVacanciesSlice = createSlice({
  name: 'jobOpenings',
  initialState: initialJobOpeningsState,
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload
    },
    setSearchString: (state, action) => {
      state.searchString = action.payload
    },
    setPaginationCache: (state, action) => {
      state.paginationCache = action.payload
    },
    setIsProc: (state, action) => {
      state.isProc = action.payload
    },
    setVendor: (state, action) => {
      state.vendor = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllJobVacancies.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllJobVacancies = action.payload.list
        state.listSize = action.payload.size
      })
      .addCase(getAllTechnology.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllTechnology = action.payload
      })
      .addCase(getJobVacancyAudit.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getJobVacancyAuditList = action.payload.list
      })
      .addCase(getJobOpeningById.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getJobOpeningById = action.payload
      })
      .addCase(updateJobVacancy.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getJobOpeningById = action.payload
      })
      .addCase(getActiveVendorsList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getVendorDetails = action.payload
      })
      .addCase(editVendorJobDetails.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllJobVacancies = action.payload.list
      })
      .addMatcher(
        isAnyOf(
          deleteJobVacancy.fulfilled,
          addJobVacancy.fulfilled,
          getAllJobVacancies.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          getAllTechnology.pending,
          getAllJobVacancies.pending,
          getJobOpeningById.pending,
          getJobVacancyAudit.pending,
          deleteJobVacancy.pending,
          addJobVacancy.pending,
          updateJobVacancy.pending,
          getActiveVendorsList.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getAllTechnology.rejected,
          getAllJobVacancies.rejected,
          getJobOpeningById.rejected,
          getJobVacancyAudit.rejected,
          deleteJobVacancy.rejected,
          addJobVacancy.rejected,
          updateJobVacancy.rejected,
          getActiveVendorsList.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})
const isLoading = (state: RootState): LoadingState =>
  state?.jobVacancies.isLoading

const getJobVacancies = (state: RootState): GetAllJobVacanciesList[] =>
  state.jobVacancies.getAllJobVacancies

const getTechnology = (state: RootState): GetAllTechnology[] =>
  state.jobVacancies.getAllTechnology

const JobOpeningById = (state: RootState): GetAllJobVacanciesList =>
  state.jobVacancies.getJobOpeningById

const JobVacancyAudit = (state: RootState): JobVacancyAuditList =>
  state.jobVacancies.getJobVacancyAudit

const auditList = (state: RootState): JobVacancyAuditList[] =>
  state.jobVacancies.getJobVacancyAuditList

const listSize = (state: RootState): number => state.jobVacancies.listSize
const searchString = (state: RootState): string =>
  state.jobVacancies.searchString
const getStatus = (state: RootState): string => state.jobVacancies.status

const getVendor = (state: RootState): boolean => state.jobVacancies.vendor

const vendorList = (state: RootState): VendorResponse[] =>
  state.jobVacancies.getVendorDetails

const getPaginationCache = (
  state: RootState,
): {
  currentPage: number
  pageSize: number
  searchJobTitle?: string
  status?: string
  vendorEmployeeId?: string
  isVendor?: boolean
} => state.jobVacancies.paginationCache

const getIsProc = (state: RootState): boolean => state.jobVacancies.isProc

export const jobVacanciesThunk = {
  getAllJobVacancies,
  getAllTechnology,
  addJobVacancy,
  deleteJobVacancy,
  getJobOpeningById,
  getJobVacancyAudit,
  updateJobVacancy,
  isCandidateMappedWithJob,
  getActiveVendorsList,
  editVendorJobDetails,
}

export const jobVacanciesSelectors = {
  isLoading,
  getJobVacancies,
  listSize,
  getTechnology,
  JobOpeningById,
  JobVacancyAudit,
  auditList,
  getStatus,
  searchString,
  getPaginationCache,
  getIsProc,
  vendorList,
  getVendor,
}

export const jobOpeningsService = {
  ...jobVacanciesThunk,
  actions: jobVacanciesSlice.actions,
  selectors: jobVacanciesSelectors,
}

export default jobVacanciesSlice.reducer
